import React from 'react';

const TermsPage: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Terms & Conditions</h1>
      <div className="bg-white rounded-lg shadow-sm p-8 prose prose-sm max-w-none">
        <p className="text-sm text-gray-500 mb-6">
          <strong>Effective Date:</strong> November 2, 2025<br />
          <strong>Last Updated:</strong> November 2, 2025
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
        <p className="text-gray-700 mb-4">
          These Terms and Conditions ("Terms") constitute a legally binding agreement between you ("User," "you," or "your") 
          and Urbanix LLC, a Michigan limited liability company doing business as Pay2Rent.me ("Pay2Rent," "we," "us," or "our").
        </p>
        <p className="text-gray-700 mb-4">
          These Terms govern your access to and use of the Pay2Rent website, platform, applications, and related services 
          (collectively, the "Services"). By accessing or using the Services, you agree to comply with and be bound by these 
          Terms and our Privacy Policy. If you do not agree, you must not access or use the Services.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Eligibility</h2>
        <p className="text-gray-700 mb-4">
          You must be at least 18 years old and capable of entering into a binding legal agreement to use the Services.
        </p>
        <p className="text-gray-700 mb-4">
          By using the Services, you represent and warrant that all information you provide is accurate, truthful, and current.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Platform Overview</h2>
        <p className="text-gray-700 mb-4">
          Pay2Rent provides a peer-to-peer rental marketplace that allows users to list, discover, and rent items directly 
          from one another. Pay2Rent acts solely as a marketplace facilitator and is not a party to rental contracts between users.
        </p>
        <p className="text-gray-700 mb-4">
          We do not own, inspect, store, or verify items listed by users, and we are not responsible for the conduct of any 
          user or the condition, legality, or suitability of any listing.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Account Registration</h2>
        <p className="text-gray-700 mb-4">
          To use certain features, you must register for an account by providing your name, email address, and other required 
          information. You are responsible for maintaining the confidentiality of your account credentials and for all activities 
          that occur under your account. You must promptly notify Pay2Rent of any unauthorized use or security breach.
        </p>
        <p className="text-gray-700 mb-4">
          We reserve the right to suspend or terminate accounts that violate these Terms or applicable law.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. User Conduct</h2>
        <p className="text-gray-700 mb-4">
          You agree to use the Services only for lawful purposes and in accordance with these Terms. You will not:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Post, rent, or request any illegal, unsafe, or prohibited items (including weapons, controlled substances, or adult services).</li>
          <li>Violate any law, regulation, or third-party right.</li>
          <li>Misrepresent your identity, listings, or any transaction details.</li>
          <li>Interfere with or damage the operation of the Services.</li>
          <li>Attempt to access data or accounts without authorization.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Pay2Rent reserves the right to remove listings, restrict access, or terminate accounts at its sole discretion if it 
          believes these Terms have been violated.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Listings and Rentals</h2>
        <p className="text-gray-700 mb-4">
          Users may create listings that include item descriptions, availability, and pricing.
        </p>
        <p className="text-gray-700 mb-4">
          By posting a listing, you represent that you have the right to rent the item and that the information is accurate and complete.
        </p>
        <p className="text-gray-700 mb-4">
          You grant Pay2Rent a non-exclusive, worldwide, royalty-free license to use, display, and distribute your listings, 
          photos, and related content for the purpose of operating and promoting the Services.
        </p>
        <p className="text-gray-700 mb-4">
          Each rental transaction constitutes a separate agreement between the renter and the owner. Pay2Rent is not a party 
          to any such agreements.
        </p>
        <p className="text-gray-700 mb-4">
          Users are solely responsible for:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Setting and communicating rental terms, including cancellation and refund policies.</li>
          <li>Ensuring compliance with all applicable laws and safety requirements.</li>
          <li>Resolving disputes directly with one another.</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Payments and Fees</h2>
        <p className="text-gray-700 mb-4">
          Payments are processed through Stripe Connect and PayPal. By using the Services, you agree to comply with the terms 
          and policies of those third-party providers.
        </p>
        <p className="text-gray-700 mb-4">
          Pay2Rent does not collect or store credit card or bank account information.
        </p>
        <p className="text-gray-700 mb-4">
          Pay2Rent may charge service or transaction fees to support platform operation, which will be disclosed at the time 
          of transaction.
        </p>
        <p className="text-gray-700 mb-4">
          You authorize Stripe and/or PayPal to process payments and transfers on your behalf in accordance with their terms.
        </p>
        <p className="text-gray-700 mb-4">
          Users are responsible for any taxes applicable to their transactions.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Refunds and Cancellations</h2>
        <p className="text-gray-700 mb-4">
          Refunds and cancellations are subject to the individual listing's policy, as determined by the listing owner. 
          Pay2Rent is not responsible for refunds or disputes arising from user-to-user transactions.
        </p>
        <p className="text-gray-700 mb-4">
          If a refund is granted through Stripe or PayPal, processing fees or delays may apply according to their respective policies.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Disputes Between Users</h2>
        <p className="text-gray-700 mb-4">
          Pay2Rent is not a party to any disputes between users and does not provide formal mediation or arbitration services. 
          Users are encouraged to resolve disagreements amicably and in good faith. If necessary, Pay2Rent may, at its sole 
          discretion, review reported issues for policy violations but does not guarantee any outcome or remedy.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Intellectual Property</h2>
        <p className="text-gray-700 mb-4">
          All content, software, and materials provided by Pay2Rent (excluding user-generated listings) are the property of 
          Urbanix LLC or its licensors and are protected by copyright, trademark, and other intellectual property laws.
        </p>
        <p className="text-gray-700 mb-4">
          You may not copy, reproduce, modify, distribute, or create derivative works from any Pay2Rent materials without 
          prior written permission.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Third-Party Services</h2>
        <p className="text-gray-700 mb-4">
          The Services may include integrations or links to third-party providers such as Stripe, PayPal, AWS, or Google reCAPTCHA. 
          These services are subject to their own terms and privacy policies, and Pay2Rent is not responsible for their practices 
          or content.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Privacy</h2>
        <p className="text-gray-700 mb-4">
          Your use of the Services is also governed by our <a href="/privacy" className="text-primary-600 hover:text-primary-700">Privacy Policy</a>, 
          available at <a href="https://pay2rent.me/privacy" className="text-primary-600 hover:text-primary-700">https://pay2rent.me/privacy</a>. 
          By using the Services, you consent to our collection and use of your information as described therein.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Prohibited Items and Activities</h2>
        <p className="text-gray-700 mb-4">
          The following items and activities are expressly prohibited on Pay2Rent:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Illegal goods or substances.</li>
          <li>Weapons, explosives, or hazardous materials.</li>
          <li>Adult or sexually explicit services.</li>
          <li>Items that infringe on another party's intellectual property rights.</li>
          <li>Any use of the platform for fraudulent, deceptive, or malicious purposes.</li>
        </ul>
        <p className="text-gray-700 mb-4">
          Pay2Rent reserves the right to remove or block any listing or user at its sole discretion.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">14. Limitation of Liability</h2>
        <p className="text-gray-700 mb-4">
          To the fullest extent permitted by law, Pay2Rent, its officers, directors, employees, and affiliates shall not be 
          liable for any indirect, incidental, consequential, or punitive damages, including but not limited to lost profits, 
          data loss, or reputation harm arising from your use of the Services.
        </p>
        <p className="text-gray-700 mb-4">
          Pay2Rent's total cumulative liability, if any, for any claim arising out of or relating to these Terms or the Services 
          shall not exceed the greater of (a) USD $100 or (b) the total amount of fees paid by you to Pay2Rent in the 12 months 
          preceding the event giving rise to the claim.
        </p>
        <p className="text-gray-700 mb-4">
          Some jurisdictions do not allow limitation of liability for certain damages, so these limitations may not apply to you.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">15. Indemnification</h2>
        <p className="text-gray-700 mb-4">
          You agree to defend, indemnify, and hold harmless Urbanix LLC (Pay2Rent.me), its officers, employees, and affiliates 
          from and against any claims, damages, losses, liabilities, costs, or expenses (including reasonable attorneys' fees) 
          arising from:
        </p>
        <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
          <li>Your use or misuse of the Services.</li>
          <li>Your violation of these Terms or applicable law.</li>
          <li>Any content or item you post, rent, or supply through the Services.</li>
        </ul>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">16. Disclaimer of Warranties</h2>
        <p className="text-gray-700 mb-4">
          The Services are provided "as is" and "as available", without warranties of any kind, express or implied. Pay2Rent 
          does not guarantee the accuracy of listings, the performance of users, or the outcome of any transaction. Use of the 
          Services is at your own risk.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">17. Termination</h2>
        <p className="text-gray-700 mb-4">
          Pay2Rent may suspend or terminate your account or access to the Services at any time, for any reason, without notice 
          or liability. Upon termination, your right to use the Services will immediately cease. Sections that, by their nature, 
          should survive termination (including Limitation of Liability, Indemnification, and Governing Law) shall survive.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">18. Governing Law and Jurisdiction</h2>
        <p className="text-gray-700 mb-4">
          These Terms are governed by the laws of the State of Michigan, United States, without regard to conflict of laws 
          principles. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state or 
          federal courts located in Michigan, and you consent to such jurisdiction and venue.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">19. International Users</h2>
        <p className="text-gray-700 mb-4">
          Users accessing the Services from outside the United States do so at their own risk and are responsible for compliance 
          with local laws. Pay2Rent makes no representation that the Services are appropriate or available in all jurisdictions.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">20. Amendments</h2>
        <p className="text-gray-700 mb-4">
          We may update these Terms from time to time. The "Last Updated" date will reflect the latest version. Continued use 
          of the Services after changes constitutes acceptance of the revised Terms.
        </p>

        <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">21. Contact Information</h2>
        <p className="text-gray-700 mb-4">
          For questions, complaints, or notices under these Terms, contact:
        </p>
        <p className="text-gray-700 mb-4">
          <strong>Urbanix LLC (d/b/a Pay2Rent.me)</strong><br />
          Email: <a href="mailto:support@pay2rent.me" className="text-primary-600 hover:text-primary-700">support@pay2rent.me</a><br />
          Jurisdiction: Michigan, United States
        </p>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-gray-600 text-center">
            © 2025 Urbanix LLC. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;

